import React, { useState, useEffect } from 'react';
import {
  Input, Button, Card, Typography, Table, Tooltip, Collapse, Row, Col, Divider, Space, Tag, Modal, Upload, message,
} from 'antd';
import {
  EditOutlined, SearchOutlined, FileTextOutlined, SettingOutlined, AppstoreOutlined, EyeOutlined, PlusOutlined, CopyOutlined, ReloadOutlined, ImportOutlined, ExportOutlined, InboxOutlined,
} from '@ant-design/icons';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { coy } from 'react-syntax-highlighter/dist/esm/styles/prism';

const { TextArea } = Input;
const { Title, Text } = Typography;
const { Panel } = Collapse;

// --- Static Data Definitions ---
const fieldDefinitions = [
  "Version", "Function", "Process Mode", "GTS Control", "Number of Authorizers", "Replace Option", "Simulation Mode", "Simulation Reference", "Activity ID", "Owner", "Start Date", "Due Date", "User", "External Process ID", "External Process", "Assign Reason", "User Role ID", "Reference"
];

const fieldSignificance = [
  "Targets a specific version in T24 (e.g., FUNDS.TRANSFER,I)",
  "Transaction type: I/A/D/R (Input, Authorise, Delete, Reverse)",
  "Validate or Process mode (V/P)",
  "Controls error/override handling (0-4)",
  "Specifies number of authorizers required",
  "Indicates if data should be replaced (REPLACE)",
  "Enables simulation without committing (SIMULATION)",
  "Reference for simulation runs",
  "Used for workflow integration",
  "Owner of the activity",
  "Start date of activity",
  "Due date of activity",
  "User assigned to activity",
  "ID of external process",
  "Name/type of external process",
  "Reason for assignment",
  "Role of the user",
  "Reference for transaction"
];

const gtsControlMap = {
  "Null": { Description: "Default behavior. Errors are rejected, Overrides are automatically approved and committed.", GTS: "Error - reject error, Override - approve automatically and commit", OFS: "Error - reject error, Override - approve automatically and commit" },
  "0": { Description: "Default behavior. Errors are rejected, Overrides are automatically approved and committed.", GTS: "Error - reject error, Override - approve automatically and commit", OFS: "Error - reject error, Override - approve automatically and commit" },
  "1": { Description: "Errors cause a Hold (80) / Overrides are Approved (00).", GTS: "Errors → Hold (code 80); Overrides → Approved (code 00)", OFS: "Errors → Hold; Overrides → Approved" },
  "2": { Description: "Errors are Rejected (90) / Overrides cause a Hold (81).", GTS: "Errors → Rejected (code 90); Overrides → Hold (code 81)", OFS: "Errors → Rejected; Overrides → Hold" },
  "3": { Description: "Both errors and overrides cause a Hold (80/81).", GTS: "Both errors and overrides → Hold", OFS: "Both errors and overrides → Hold" },
  "4": { Description: "All records are placed in Hold; no validation or processing is performed.", GTS: "Not supported in GTS; use GTS.MODE instead", OFS: "All records placed in Hold; no validation or processing" }
};

// --- Component Definition ---
function OFSVisualizer() {
  const [ofsMessage, setOfsMessage] = useState('');
  const [parsedData, setParsedData] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [showOptions, setShowOptions] = useState(false);
  const [bulkModalVisible, setBulkModalVisible] = useState(false);
  const [bulkText, setBulkText] = useState('');
  const [bulkFor, setBulkFor] = useState(null); // 'app' or group id
  const [importModalVisible, setImportModalVisible] = useState(false);

  // Safely re-index a group to ensure keys are sequential starting from 1
  const reindexGroup = (group, namesObj = {}, valuesObj = {}) => {
    const newNames = {};
    const newValues = {};
    const existingKeys = new Set([
      ...Object.keys(namesObj).map(Number),
      ...Object.keys(valuesObj).map(Number)
    ]);

    const keys = Array.from(existingKeys).sort((a, b) => a - b);
    let idx = 1;

    keys.forEach(oldKey => {
      newNames[idx] = namesObj[oldKey] || '';
      newValues[idx] = valuesObj[oldKey] || '';
      idx++;
    });

    return { newNames, newValues };
  };

  // Re-index all groups to ensure stability and sequence
  const reindexAllProperties = (fieldNames, fieldValues) => {
    const fn = {};
    const fv = {};

    const allGroups = new Set([
      ...Object.keys(fieldNames || {}),
      ...Object.keys(fieldValues || {})
    ]);

    allGroups.forEach(group => {
      const { newNames, newValues } = reindexGroup(group, fieldNames[group] || {}, fieldValues[group] || {});
      fn[group] = newNames;
      fv[group] = newValues;
    });

    return { fn, fv };
  };

  const parseOFS = (message) => {
    // OFS structure: APPLICATION,OPTIONS,USER.INFO,ID.INFO,APP.FIELD=VALUE,...
    const parts = message.split(',').map(p => p.trim());

    const header = {
      AppName: parts[0] || '',
      options: parts[1] || '',
      UserInfo: parts[2] || '',
      IdInfo: parts[3] || '',
    };

    const appFields = [];
    const properties = {}; // PROPERTY:n -> label
    const fieldNames = {}; // group -> { index: name }
    const fieldValues = {}; // group -> { index: value }
    const contentParts = parts.slice(4).filter(p => p !== '');

    contentParts.forEach(part => {
      if (part.includes('=') && !part.startsWith('PROPERTY:') && !part.startsWith('FIELD.NAME:') && !part.startsWith('FIELD.VALUE:')) {
        // App key=value pairs
        const [key, ...rest] = part.split('=');
        const value = rest.join('=');
        appFields.push({ key: key.trim(), value: value.trim() });
      } else if (part.startsWith('PROPERTY:') && part.includes('=')) {
        // PROPERTY:n:m=label (n is group id, m is seq - we only care about n)
        const [key, ...rest] = part.split('=');
        const value = rest.join('=');
        const match = key.match(/PROPERTY:(\d+):\d+/);
        if (match) properties[match[1]] = value.trim();
      } else if (part.startsWith('FIELD.NAME:') && part.includes('=')) {
        // FIELD.NAME:group:index=name
        const [key, ...rest] = part.split('=');
        const value = rest.join('=');
        const match = key.match(/FIELD.NAME:(\d+):(\d+)/);
        if (match) {
          const [_, group, index] = match;
          if (!fieldNames[group]) fieldNames[group] = {};
          fieldNames[group][index] = value.trim();
        }
      } else if (part.startsWith('FIELD.VALUE:') && part.includes('=')) {
        // FIELD.VALUE:group:index=value
        const [key, ...rest] = part.split('=');
        const value = rest.join('=');
        const match = key.match(/FIELD.VALUE:(\d+):(\d+)/);
        if (match) {
          const [_, group, index] = match;
          if (!fieldValues[group]) fieldValues[group] = {};
          fieldValues[group][index] = value.trim();
        }
      }
    });

    // Run re-indexing to normalize the structure before setting state
    const { fn, fv } = reindexAllProperties(fieldNames, fieldValues);

    setParsedData({
      header,
      appFields,
      properties,
      fieldNames: fn,
      fieldValues: fv
    });
  };

  const reconstructOFS = () => {
    if (!parsedData) return '';
    const { header, appFields, properties, fieldNames, fieldValues } = parsedData;

    const headerStr = [header.AppName, header.options, header.UserInfo, header.IdInfo].join(',');
    const appStr = (appFields || []).map(({ key, value }) => `${key}=${value}`).join(',');

    const propParts = [];
    
    // Sort property groups numerically for stable output
    const sortedGroups = Object.keys(properties || {}).sort((a, b) => Number(a) - Number(b));

    sortedGroups.forEach((group) => {
      const label = properties[group];
      // PROPERTY:group:1=label (using 1 for sequence as it's common)
      propParts.push(`PROPERTY:${group}:1=${label}`);

      const names = fieldNames[group] || {};
      const values = fieldValues[group] || {};

      // Get all unique indexes (1, 2, 3...) present in either names or values
      const allIndexes = new Set([
        ...Object.keys(names).map(Number),
        ...Object.keys(values).map(Number)
      ]);

      Array.from(allIndexes).sort((a, b) => a - b).forEach(idx => {
        const name = names[idx] || '';
        const val = values[idx] || '';
        
        // Only include if either name or value is present to avoid long empty lists
        if (name || val) {
            propParts.push(`FIELD.NAME:${group}:${idx}=${name}`);
            propParts.push(`FIELD.VALUE:${group}:${idx}=${val}`);
        }
      });
    });

    const allParts = [headerStr, appStr, ...propParts].filter(p => p !== '').join(',');
    return allParts;
  };

  const handleParse = () => {
    parseOFS(ofsMessage);
  };

  const handleAddAppField = (count = 1) => {
    if (!parsedData) {
      message.error('Please parse a message first.');
      return;
    }
    const additions = Array.from({ length: count }).map(() => ({ key: '', value: '' }));
    setParsedData(prev => ({ ...prev, appFields: [...(prev.appFields || []), ...additions] }));
  };

  const handleBulkAdd = () => {
    if (!parsedData || !bulkFor) return;
    const lines = bulkText.split('\n').map(l => l.trim()).filter(l => l.includes('='));
    const pairs = lines.map(line => {
      const [key, ...rest] = line.split('=');
      return { key: (key || '').trim(), value: (rest.join('=') || '').trim() };
    });

    if (bulkFor === 'app') {
      setParsedData(prev => ({ ...prev, appFields: [...(prev.appFields || []), ...pairs] }));
    } else {
      // bulkFor is a group id
      setParsedData(prev => {
        const updated = { ...prev };
        const currentFieldNames = updated.fieldNames[bulkFor] || {};
        const currentFieldValues = updated.fieldValues[bulkFor] || {};

        const existingIndexes = Object.keys(currentFieldNames).map(Number);
        let next = existingIndexes.length > 0 ? Math.max(...existingIndexes) + 1 : 1;

        pairs.forEach(p => {
          currentFieldNames[next] = p.key;
          currentFieldValues[next] = p.value;
          next++;
        });

        updated.fieldNames[bulkFor] = currentFieldNames;
        updated.fieldValues[bulkFor] = currentFieldValues;

        // Reindex to ensure consistency
        const { fn, fv } = reindexAllProperties(updated.fieldNames, updated.fieldValues);
        updated.fieldNames = fn;
        updated.fieldValues = fv;
        return updated;
      });
    }
    setBulkModalVisible(false);
    setBulkText('');
    setBulkFor(null);
  };

  const handleAddPropertyFieldInline = (group, count = 1) => {
    if (!parsedData) return;
    setParsedData(prev => {
      const updated = { ...prev };
      if (!updated.fieldNames[group]) updated.fieldNames[group] = {};
      if (!updated.fieldValues[group]) updated.fieldValues[group] = {};

      const existingNames = updated.fieldNames[group] || {};
      const existingValues = updated.fieldValues[group] || {};

      const existingIndexes = new Set([
        ...Object.keys(existingNames).map(Number),
        ...Object.keys(existingValues).map(Number)
      ]);

      let next = existingIndexes.size > 0 ? Math.max(...Array.from(existingIndexes)) + 1 : 1;

      for (let i = 0; i < count; i++) {
        updated.fieldNames[group][next] = '';
        updated.fieldValues[group][next] = '';
        next++;
      }
      
      // Reindex to ensure consistency
      const { fn, fv } = reindexAllProperties(updated.fieldNames, updated.fieldValues);
      updated.fieldNames = fn;
      updated.fieldValues = fv;
      return updated;
    });
  };

  const handleCopy = async () => {
    const text = reconstructOFS();
    try {
      // Use document.execCommand('copy') for better compatibility in iFrames
      const el = document.createElement('textarea');
      el.value = text;
      document.body.appendChild(el);
      el.select();
      document.execCommand('copy');
      document.body.removeChild(el);
      message.success('OFS copied to clipboard');
    } catch (e) {
      message.error('Failed to copy message');
      console.error(e);
    }
  };

  const handleReset = () => {
    setOfsMessage('');
    setParsedData(null);
    setSearchTerm('');
    message.info('Visualizer reset.');
  };

  const handleExportJSON = () => {
    if (!parsedData) {
      message.error('No data to export.');
      return;
    }
    const blob = new Blob([JSON.stringify(parsedData, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `ofs_${parsedData.header.AppName || 'data'}_${Date.now()}.json`;
    link.click();
    URL.revokeObjectURL(url);
    message.success('JSON exported successfully.');
  };

  const handleImportJSON = (file) => {
    setImportModalVisible(false);
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const data = JSON.parse(e.target.result);
        if (!data || !data.header || typeof data.header.AppName !== 'string') {
          message.error('Invalid JSON structure. Missing key header fields.');
          return;
        }
        
        // Run re-indexing to normalize the imported structure
        const { fn, fv } = reindexAllProperties(data.fieldNames || {}, data.fieldValues || {});

        setParsedData({ ...data, fieldNames: fn, fieldValues: fv });
        message.success('OFS structure imported successfully.');
      } catch (err) {
        message.error('Invalid JSON file format.');
        console.error('Import error:', err);
      }
    };
    reader.onerror = () => {
        message.error('Failed to read file.');
    };
    reader.readAsText(file);
    return false; // Prevent Ant Design from uploading the file
  };

  const renderOptionsTable = () => {
    const options = (parsedData?.header.options || '').split('/') || [];
    const data = fieldDefinitions.map((field, i) => ({
      key: i,
      field: field,
      value: options[i] || '',
      description: fieldSignificance[i] || 'N/A'
    }));

    // Ensure the table includes all options even if they exceed the static definitions
    if (options.length > fieldDefinitions.length) {
        for (let i = fieldDefinitions.length; i < options.length; i++) {
            data.push({
                key: i,
                field: `Option ${i + 1}`,
                value: options[i] || '',
                description: 'Custom/Unidentified Option'
            });
        }
    }

    return (
      <Table
        columns={[
          { title: 'Field', dataIndex: 'field', width: '20%' },
          {
            title: 'Value',
            dataIndex: 'value',
            width: '30%',
            render: (text, record) => (
              <Input
                value={text}
                onChange={(e) => {
                  const newOptions = [...options];
                  // Ensure array is long enough to set the value
                  for (let i = 0; i <= record.key; i++) {
                      if (newOptions[i] === undefined) {
                          newOptions[i] = '';
                      }
                  }
                  newOptions[record.key] = e.target.value;
                  // Rejoin and update the options string
                  setParsedData(prev => ({
                    ...prev,
                    header: { ...prev.header, options: newOptions.join('/') }
                  }));
                }}
              />
            )
          },
          { title: 'Significance', dataIndex: 'description', width: '50%' }
        ]}
        dataSource={data}
        pagination={false}
        bordered
        size="small"
      />
    );
  };

  const renderGtsControlInfo = () => {
    const options = (parsedData?.header.options || '').split('/') || [];
    const controlVal = options[3] || '0'; // GTS Control is the 4th segment (index 3). Default to 0/Null.
    const info = gtsControlMap[controlVal] || gtsControlMap['0']; // Fallback to 0

    return (
      <Card size="small" title={<Space><SettingOutlined /> GTS Control ({controlVal})</Space>} bordered style={{ backgroundColor: '#e6f7ff', borderColor: '#91d5ff' }}>
        <Text type="secondary">The GTS Control value determines how errors and overrides are handled by the system.</Text>
        <p style={{ marginTop: 8 }}><b>Description:</b> {info.Description}</p>
        <p><b>GTS (Global Transaction Service):</b> {info.GTS}</p>
        <p><b>OFS (Open Financial Services):</b> {info.OFS}</p>
        <Text type="danger" style={{ fontSize: '0.8em', display: 'block', marginTop: 8 }}>
            Note: The GTS Control setting is critical for transaction authorization and error handling.
        </Text>
      </Card>
    );
  };
  
  const handleAppFieldDelete = (idxToDelete) => {
    setParsedData(prev => {
        const updatedFields = prev.appFields.filter((_, idx) => idx !== idxToDelete);
        return { ...prev, appFields: updatedFields };
    });
    message.success('Field deleted.');
  };
  
  const handlePropertyFieldDelete = (group, indexToDelete) => {
    setParsedData(prev => {
        const updated = { ...prev };
        
        // Delete the entry from both fieldNames and fieldValues
        if (updated.fieldNames[group]) {
            delete updated.fieldNames[group][indexToDelete];
        }
        if (updated.fieldValues[group]) {
            delete updated.fieldValues[group][indexToDelete];
        }

        // Re-index to ensure keys are sequential again (1, 2, 3...)
        const { fn, fv } = reindexAllProperties(updated.fieldNames, updated.fieldValues);
        updated.fieldNames = fn;
        updated.fieldValues = fv;
        
        return updated;
    });
    message.success(`Field ${indexToDelete} in Group ${group} deleted and reindexed.`);
  };


  return (
    <Card bordered style={{ margin: '20px', background: '#f0f5ff', maxWidth: '100%', overflowX: 'auto' }}>
      <Title level={3} style={{ color: '#2f54eb', marginBottom: 20 }}>T24 OFS Message Visualizer & Editor</Title>
      
      <TextArea
        rows={4}
        placeholder="Paste your OFS message here (e.g., FUNDS.TRANSFER,I/PROCESS//1,USER1/ID,DEBIT.ACCT=1234,CREDIT.ACCT=5678,...)"
        value={ofsMessage}
        onChange={(e) => setOfsMessage(e.target.value)}
        style={{ marginBottom: 15 }}
      />
      
      <Space wrap style={{ marginTop: 10, marginBottom: 20 }}>
        <Button type="primary" icon={<SearchOutlined />} onClick={handleParse} disabled={!ofsMessage.trim()}>Parse OFS</Button>
        <Button icon={<CopyOutlined />} onClick={handleCopy} disabled={!parsedData}>Copy Message</Button>
        <Button icon={<ReloadOutlined />} onClick={handleReset}>Reset</Button>
        <Divider type="vertical" />
        <Button icon={<ExportOutlined />} onClick={handleExportJSON} disabled={!parsedData}>Export JSON</Button>
        <Button icon={<ImportOutlined />} onClick={() => setImportModalVisible(true)}>Import JSON</Button>
      </Space>

      {parsedData && (
        <>
          <Divider />
          
          {/* Header Fields */}
          <Title level={4}><SettingOutlined /> Header Fields</Title>
          <Row gutter={[16, 16]}>
            {Object.entries(parsedData.header).map(([key, val]) => (
              <Col xs={24} sm={12} md={6} key={key}>
                <Input
                  addonBefore={key}
                  value={val}
                  onChange={(e) => setParsedData(prev => ({ ...prev, header: { ...prev.header, [key]: e.target.value } }))}
                  style={{ width: '100%' }}
                />
              </Col>
            ))}
            <Col xs={24} sm={12} md={6}>
              <Button icon={<EyeOutlined />} onClick={() => setShowOptions(true)} style={{ width: '100%', height: '100%' }}>View Options Breakdown</Button>
            </Col>
          </Row>

          <Modal
            title="OFS Options Breakdown (Header Segment 2)"
            open={showOptions}
            onCancel={() => setShowOptions(false)}
            footer={null}
            width={800}
          >
            {renderOptionsTable()}
          </Modal>

          <Divider />

          {/* GTS Control Info Card */}
          {parsedData.header.options && renderGtsControlInfo()}
          <Divider />

          {/* Application Fields */}
          <Title level={4}><AppstoreOutlined /> Application Fields (Key=Value Pairs)</Title>
          <Row gutter={[16, 16]}>
            {(parsedData.appFields || []).map((field, idx) => (
              <Col xs={24} md={12} key={`app-${idx}`}>
                <Space.Compact style={{ width: '100%' }}>
                  <Input
                    placeholder="Field Name"
                    value={field.key}
                    onChange={(e) => {
                      const newKey = e.target.value;
                      setParsedData(prev => {
                        const updated = { ...prev };
                        updated.appFields = [...updated.appFields];
                        updated.appFields[idx] = { ...updated.appFields[idx], key: newKey };
                        return updated;
                      });
                    }}
                    style={{ flex: 1.5 }}
                  />
                  <Input
                    placeholder="Field Value"
                    value={field.value}
                    onChange={(e) => {
                      const newVal = e.target.value;
                      setParsedData(prev => {
                        const updated = { ...prev };
                        updated.appFields = [...updated.appFields];
                        updated.appFields[idx] = { ...updated.appFields[idx], value: newVal };
                        return updated;
                      });
                    }}
                    style={{ flex: 2 }}
                  />
                   <Tooltip title="Delete Field">
                      <Button type="text" danger icon={<EditOutlined />} onClick={() => handleAppFieldDelete(idx)} />
                  </Tooltip>
                </Space.Compact>
              </Col>
            ))}
          </Row>
          <Space style={{ marginTop: 15 }}>
            <Button icon={<PlusOutlined />} onClick={() => handleAddAppField(1)}>Add 1 Field</Button>
            <Button icon={<PlusOutlined />} onClick={() => handleAddAppField(3)}>Add 3 Fields</Button>
            <Button icon={<PlusOutlined />} onClick={() => { setBulkFor('app'); setBulkModalVisible(true); }}>Bulk Add</Button>
          </Space>

          <Divider />

          {/* Property Groups (Multi-valued/Sub-valued Fields) */}
          <Title level={4}><AppstoreOutlined /> Multi-Value Property Groups</Title>
          <Input 
            placeholder="Filter field name within groups..." 
            value={searchTerm} 
            onChange={(e) => setSearchTerm(e.target.value.toLowerCase())} 
            style={{ marginBottom: 15 }}
            prefix={<SearchOutlined />}
          />
          
          <Collapse accordion style={{ marginTop: 10 }}>
            {Object.entries(parsedData.properties || {}).map(([group, prop]) => {
              const namesForGroup = parsedData.fieldNames[group] || {};
              const valuesForGroup = parsedData.fieldValues[group] || {};
              
              // Get all indexes that have a name or value
              const allIndexes = new Set([
                ...Object.keys(namesForGroup).map(Number),
                ...Object.keys(valuesForGroup).map(Number)
              ]);

              const rows = Array.from(allIndexes).sort((a, b) => a - b).map(index => {
                  const name = namesForGroup[index];
                  const value = valuesForGroup[index];
                  return { key: index, index, name, value };
              }).filter(row => 
                  (row.name || '').toLowerCase().includes(searchTerm) || 
                  (row.value || '').toLowerCase().includes(searchTerm)
              );

              const columns = [
                { 
                  title: 'Field Index', 
                  dataIndex: 'index', 
                  width: '10%',
                  render: (text) => <Tag color="geekblue">{text}</Tag>
                },
                { 
                  title: 'Field Name', 
                  dataIndex: 'name', 
                  width: '40%',
                  render: (text, record) => (
                    <Input 
                      value={text || ''} 
                      placeholder="FIELD.NAME:n:m" 
                      onChange={(e) => {
                        const newVal = e.target.value;
                        setParsedData(prev => {
                          const updated = { ...prev };
                          if (!updated.fieldNames[group]) updated.fieldNames[group] = {};
                          updated.fieldNames[group][record.index] = newVal;
                          const { fn, fv } = reindexAllProperties(updated.fieldNames, updated.fieldValues);
                          updated.fieldNames = fn;
                          updated.fieldValues = fv;
                          return updated;
                        });
                      }} 
                    />
                  )
                },
                { 
                  title: 'Field Value', 
                  dataIndex: 'value', 
                  width: '40%',
                  render: (text, record) => (
                    <Input 
                      value={text || ''} 
                      placeholder="FIELD.VALUE:n:m" 
                      onChange={(e) => {
                        const newVal = e.target.value;
                        setParsedData(prev => {
                          const updated = { ...prev };
                          if (!updated.fieldValues[group]) updated.fieldValues[group] = {};
                          updated.fieldValues[group][record.index] = newVal;
                          const { fn, fv } = reindexAllProperties(updated.fieldNames, updated.fieldValues);
                          updated.fieldNames = fn;
                          updated.fieldValues = fv;
                          return updated;
                        });
                      }} 
                    />
                  )
                },
                {
                    title: 'Action',
                    key: 'action',
                    width: '10%',
                    render: (_, record) => (
                        <Tooltip title="Delete Row">
                            <Button type="text" danger icon={<EditOutlined />} onClick={() => handlePropertyFieldDelete(group, record.index)} />
                        </Tooltip>
                    ),
                }
              ];

              return (
                <Panel header={<Tag color="blue">Property Group {group}: {prop}</Tag>} key={group}>
                  <Space style={{ marginBottom: 15 }}>
                    <Button icon={<PlusOutlined />} onClick={() => handleAddPropertyFieldInline(group, 1)}>Add 1 Row</Button>
                    <Button icon={<PlusOutlined />} onClick={() => handleAddPropertyFieldInline(group, 3)}>Add 3 Rows</Button>
                    <Button icon={<PlusOutlined />} onClick={() => { setBulkFor(group); setBulkModalVisible(true); }}>Bulk Add</Button>
                  </Space>
                  <Table 
                    columns={columns} 
                    dataSource={rows} 
                    pagination={false} 
                    size="small"
                    scroll={{ x: 'max-content' }}
                  />
                </Panel>
              );
            })}
          </Collapse>

          <Divider />

          {/* Reconstructed OFS Output */}
          <Title level={4}><FileTextOutlined /> Final OFS Message</Title>
          <div style={{ background: '#2d2d2d', padding: 15, borderRadius: 8, overflowX: 'auto' }}>
            <SyntaxHighlighter language="text" style={coy} customStyle={{ margin: 0, padding: 0 }}>
              {reconstructOFS()}
            </SyntaxHighlighter>
          </div>
        </>
      )}

      {/* Bulk Add Modal */}
      <Modal
        title={`Bulk Add Fields ${bulkFor === 'app' ? '(Application Fields)' : bulkFor ? `(Property Group ${bulkFor})` : ''}`}
        open={bulkModalVisible}
        onOk={handleBulkAdd}
        onCancel={() => { setBulkModalVisible(false); setBulkText(''); setBulkFor(null); }}
        okText="Add Fields"
        destroyOnClose={true}
      >
        <p style={{ marginBottom: 10 }}>Enter one key=value pair per line. Only lines containing `=` will be processed.</p>
        <TextArea
          rows={6}
          placeholder={`Enter one key=value per line. Example:\nFIELD.NAME=VALUE\nCURRENCY=USD`}
          value={bulkText}
          onChange={(e) => setBulkText(e.target.value)}
        />
      </Modal>

      {/* Import JSON Modal */}
      <Modal
        title="Import Parsed OFS JSON"
        open={importModalVisible}
        onCancel={() => setImportModalVisible(false)}
        footer={null}
        destroyOnClose={true} 
      >
        <Upload.Dragger
            name="file"
            multiple={false}
            accept=".json"
            beforeUpload={handleImportJSON} 
            showUploadList={false} 
        >
            <p className="ant-upload-drag-icon">
                <InboxOutlined />
            </p>
            <p className="ant-upload-text">Click or drag JSON file to this area to import</p>
            <p className="ant-upload-hint">
                Imported data must be in the same structure as the exported JSON.
            </p>
        </Upload.Dragger>
      </Modal>

    </Card>
  );
}

export default OFSVisualizer;